import { html } from 'lit-html';
import { get } from '../api.js';
import { app, emptyRow, formValues, helpLink, type Page } from '../app.js';
import { duration, STATUS_LABEL, statusPill, when } from '../format.js';
import { agentHref } from './reports.js';

interface Timeout {
  id: string;
  conversationId: string;
  agentId: string;
  agentName: string | null;
  queueId: string;
  queueName: string | null;
  status: string;
  genesysEventTime: string;
  detectedAt: string;
  deactivatedAt: string | null;
  queueReactivatedAt: string | null;
  reactivationSource: string | null;
  inactiveDurationSeconds: number | null;
  outcomeUncertain: boolean;
  lastError: string | null;
  sourceTopic: string;
  policy: { reason?: string; outcome?: string; wouldAction?: string; dryRunReasons?: string[]; graceCount?: number | null } | null;
}

const filters = { since: '7d', status: '', agentId: '', queueId: '' };

/** Opens this page filtered to one agent (from the agent report). */
export function showAgentTimeouts(agentId: string): void {
  Object.assign(filters, { since: '90d', status: '', agentId, queueId: '' });
  location.hash = '#/timeouts';
}
let rows: Timeout[] = [];
let open: string | null = null;

function detail(t: Timeout) {
  const p = t.policy ?? {};
  return html`<tr class="detail"><td colspan="5">
    <dl class="kv">
      <dt>Why</dt><dd>${p.reason ?? '—'}${p.wouldAction ? html` · would: <b>${p.wouldAction}</b>` : ''}</dd>
      <dt>Timed out</dt><dd>${when(t.genesysEventTime)} <span class="muted">(${t.sourceTopic} event, detected ${when(t.detectedAt)})</span></dd>
      <dt>Deactivated</dt><dd>${when(t.deactivatedAt)}${t.outcomeUncertain ? html` <span class="pill warning">inferred</span>` : ''}</dd>
      <dt>Reactivated</dt><dd>${when(t.queueReactivatedAt)}${t.reactivationSource ? html` <span class="muted">(${t.reactivationSource})</span>` : ''}</dd>
      <dt>Inactive for</dt><dd>${duration(t.inactiveDurationSeconds)}</dd>
      ${t.lastError ? html`<dt>Note</dt><dd>${t.lastError}</dd>` : ''}
      <dt>Conversation</dt><dd><code>${t.conversationId}</code></dd>
      <dt>Agent / queue ID</dt><dd><code>${t.agentId}</code> · <code>${t.queueId}</code></dd>
      <dt>Agent report</dt><dd><a href=${agentHref(t.agentId)}>${t.agentName ?? t.agentId}: metrics and history →</a></dd>
    </dl>
  </td></tr>`;
}

export const timeouts: Page = {
  title: 'Timeouts',
  async load() {
    const q = new URLSearchParams({ since: filters.since, limit: '300' });
    if (filters.status) q.set('status', filters.status);
    if (filters.agentId) q.set('agentId', filters.agentId);
    if (filters.queueId) q.set('queueId', filters.queueId);
    rows = (await get<{ timeouts: Timeout[] }>(`/timeouts?${q}`)).timeouts;
  },
  view() {
    const apply = async (e: Event) => {
      e.preventDefault();
      Object.assign(filters, formValues(e.target as HTMLFormElement));
      await timeouts.load();
      app.rerender();
    };
    return html`
      <h1>Timeouts ${helpLink('timeouts')}</h1>
      <p class="hint">Every ININ-WRAP-UP-TIMEOUT the service has seen, and what it did. Select a row for the details.</p>
      <form class="filters compact" @submit=${apply}>
        <label>Period <select name="since">
          ${[['24h', 'Last 24 hours'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'], ['90d', 'Last 90 days']].map(([v, l]) => html`<option value=${v!} ?selected=${filters.since === v}>${l}</option>`)}
        </select></label>
        <label>Outcome <select name="status">
          <option value="">Any</option>
          ${Object.entries(STATUS_LABEL).map(([v, l]) => html`<option value=${v} ?selected=${filters.status === v}>${l}</option>`)}
        </select></label>
        <label>Agent ID <input name="agentId" .value=${filters.agentId} placeholder="Genesys user ID"></label>
        <label>Queue ID <input name="queueId" .value=${filters.queueId} placeholder="Genesys queue ID"></label>
        <button class="btn compact">Apply</button>
      </form>
      <div class="table-wrap"><table class="clickable">
        <thead><tr><th>When</th><th>Agent</th><th>Queue</th><th>Outcome</th><th>Inactive for</th></tr></thead>
        <tbody>${rows.length
          ? rows.map((t) => html`
              <tr @click=${() => { open = open === t.id ? null : t.id; app.rerender(); }} class=${open === t.id ? 'selected' : ''}>
                <td class="nowrap">${when(t.genesysEventTime)}</td><td>${t.agentName ?? t.agentId}</td><td>${t.queueName ?? t.queueId}</td>
                <td>${statusPill(t.status)}</td><td>${duration(t.inactiveDurationSeconds)}</td>
              </tr>
              ${open === t.id ? detail(t) : ''}`)
          : emptyRow(5, 'No timeouts match these filters.')}</tbody>
      </table></div>
      <p class="muted">${rows.length === 300 ? 'Showing the latest 300.' : `${rows.length} shown.`}</p>`;
  },
};
